import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import { BidCard } from "./BidCard";
import type { Bid } from "@/lib/types";
import { ArrowUpDown, TrendingDown, Star, BarChart3 } from "lucide-react";
import { cn } from "@/lib/utils";

type SortMode = "reputation" | "price" | "rating" | "newest";

interface BidComparisonProps {
	bids: Bid[];
	budget: number;
}

function computeReputationScore(bid: Bid, allBids: Bid[], budget: number): number {
	if (allBids.length === 0) return 0;
	const amounts = allBids.map((b) => b.amount);
	const minAmount = Math.min(...amounts);
	const maxAmount = Math.max(...amounts);
	const range = maxAmount - minAmount || 1;

	// Price competitiveness (0-1, lower is better)
	const priceScore = 1 - (bid.amount - minAmount) / range;

	// Rating score (0-1)
	const ratingScore = bid.provider.rating / 5;

	// Experience score (0-1, log scale)
	const maxJobs = Math.max(...allBids.map((b) => b.provider.completedJobs));
	const expScore = maxJobs > 0 ? Math.log(1 + bid.provider.completedJobs) / Math.log(1 + maxJobs) : 0;

	// Budget proximity bonus (reward bids closer to budget without exceeding)
	const budgetScore = bid.amount <= budget ? 1 : 0;

	// Weighted combination
	return (priceScore * 30 + ratingScore * 35 + expScore * 25 + budgetScore * 10) / 10;
}

function sortBids(bids: Bid[], mode: SortMode, budget: number): { bid: Bid; score: number }[] {
	const scored = bids.map((bid) => ({
		bid,
		score: computeReputationScore(bid, bids, budget),
	}));

	switch (mode) {
		case "reputation":
			scored.sort((a, b) => b.score - a.score);
			break;
		case "price":
			scored.sort((a, b) => a.bid.amount - b.bid.amount);
			break;
		case "rating":
			scored.sort((a, b) => b.bid.provider.rating - a.bid.provider.rating);
			break;
		case "newest":
			scored.sort(
				(a, b) =>
					new Date(b.bid.createdAt).getTime() - new Date(a.bid.createdAt).getTime()
			);
			break;
	}

	return scored;
}

const sortOptions: { mode: SortMode; label: string; icon: typeof ArrowUpDown }[] = [
	{ mode: "reputation", label: "Best Match", icon: BarChart3 },
	{ mode: "price", label: "Lowest Price", icon: TrendingDown },
	{ mode: "rating", label: "Top Rated", icon: Star },
	{ mode: "newest", label: "Newest", icon: ArrowUpDown },
];

export function BidComparison({ bids, budget }: BidComparisonProps) {
	const [sortMode, setSortMode] = useState<SortMode>("reputation");
	const sorted = sortBids(bids, sortMode, budget);
	const lowestAmount = bids.length > 0 ? Math.min(...bids.map((b) => b.amount)) : 0;

	if (bids.length === 0) {
		return (
			<div className="text-center py-12 text-muted-foreground">
				<BarChart3 className="h-12 w-12 mx-auto mb-3 opacity-30" />
				<p className="text-lg font-medium">No bids yet</p>
				<p className="text-sm mt-1">Be the first to submit a competitive bid</p>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			<div className="flex items-center justify-between">
				<div className="flex items-center gap-2">
					<h3 className="font-semibold text-sm">
						{bids.length} Bid{bids.length !== 1 && "s"}
					</h3>
					<Badge variant="outline" className="text-xs tabular-nums">
						Avg ${Math.round(bids.reduce((s, b) => s + b.amount, 0) / bids.length).toLocaleString()}
					</Badge>
				</div>

				<div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
					{sortOptions.map(({ mode, label, icon: Icon }) => (
						<button
							key={mode}
							onClick={() => setSortMode(mode)}
							className={cn(
								"flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-medium transition-colors",
								sortMode === mode
									? "bg-background text-foreground shadow-sm"
									: "text-muted-foreground hover:text-foreground"
							)}
						>
							<Icon className="h-3 w-3" />
							<span className="hidden sm:inline">{label}</span>
						</button>
					))}
				</div>
			</div>

			<div className="space-y-2">
				{sorted.map(({ bid, score }, i) => (
					<BidCard
						key={bid.id}
						bid={bid}
						rank={i + 1}
						lowestAmount={lowestAmount}
						budget={budget}
						reputationScore={score}
					/>
				))}
			</div>
		</div>
	);
}
